import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { request } from "../../../config/helpers/axios-instance";
import OpenBoxPhotoField from "../../../components/delivery/OpenBoxPhotoField";

// Add open-box photos to a delivery AFTER it was created.
//
// The photo is optional at creation time (product decision, 2026-09-23), so a shipment can
// exist without one and someone has to be able to attach it later — that is what this is.
//
// Reuses OpenBoxPhotoField, the same uploader the Create Delivery modal uses, so the upload
// path, validation and blob-name handling are identical in both places.
//
// Saves through PUT /delivery/admin/id/:id, the generic admin update. That endpoint can only
// write what Constants.UpdateAttributes allows (deliveryBoxImages + tenantId), so this is
// not a route onto status or courier data.
//
// REPLACE, NOT APPEND: the endpoint sets the array it is given. The modal therefore seeds
// itself with the photos already on the delivery and submits the full list, so removing an
// existing photo here genuinely removes it.
function AddBoxImagesModal({ delivery, onClose, onSaved }) {
    const existing = delivery?.deliveryBoxImages || [];
    const existingUrls = delivery?.deliveryBoxImageUrls || [];

    const [images, setImages] = useState(existing);
    const [previews, setPreviews] = useState(existingUrls);
    const [saving, setSaving] = useState(false);

    const dirty =
        images.length !== existing.length || images.some((name, i) => name !== existing[i]);

    const save = async () => {
        setSaving(true);
        try {
            const result = await request({
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                url: `delivery/admin/id/${delivery.id}`,
                data: { deliveryBoxImages: images },
            });

            if (result.data?.success) {
                toast.success(images.length ? "Photos saved" : "Photos removed");
                if (onSaved) onSaved();
                onClose();
                return;
            }
            toast.error(result.data?.err || "Couldn't save photos");
        } catch (error) {
            toast.error("Couldn't save photos");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-[0.9rem] min-w-[460px]">
            <div className="flex justify-between items-center">
                <span className="text-[1.5rem] font-bold">Open Box Photos</span>
                <span className="text-[1.2rem]">
                    Delivery ID: <span className="text-[1.2rem] font-semibold">{`#${delivery?.id}`}</span>
                </span>
            </div>
            <hr />

            <OpenBoxPhotoField
                images={images}
                previews={previews}
                onChange={(nextImages, nextPreviews) => {
                    setImages(nextImages);
                    setPreviews(nextPreviews);
                }}
            />

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="px-5 py-2 rounded-xl border border-gray-300 text-[1.2rem] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    Close
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !dirty}
                    title={dirty ? undefined : "Nothing has changed"}
                    className="px-5 py-2 rounded-xl bg-[#00A48B] text-[1.2rem] font-semibold text-white-50 hover:bg-[#008f79] disabled:opacity-50"
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );
}

AddBoxImagesModal.propTypes = {
    delivery: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
};

export default AddBoxImagesModal;
